import { z } from 'zod';
import { RoleKey } from './me.js';

/**
 * Notification choices (APP-092): My preferences › Notifications (SET1, K24) and System
 * administration › Notification emails (SYS5). The server works out every row with the domain's
 * policy, so a screen never shows a switch that does nothing.
 */

/** A catalog event key (`award_announced`). */
export const NotificationEventKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,63}$/)
  .max(64);

/** One person's choice for one notice. */
export const NotificationChoice = z
  .object({ email: z.boolean(), inApp: z.boolean(), teams: z.boolean().optional() })
  .strict();
export type NotificationChoice = z.infer<typeof NotificationChoice>;

/**
 * A change to one notice's choice: only the channels switched (APP-138 review). The server keeps
 * the rest as stored, or as the notice starts, so a channel the person can't change (an email off
 * for everyone, or required) never has to be named to switch another. A whole choice still fits.
 */
export const NotificationChoiceChange = z
  .object({
    email: z.boolean().optional(),
    inApp: z.boolean().optional(),
    teams: z.boolean().optional(),
  })
  .strict()
  .refine((c) => c.email !== undefined || c.inApp !== undefined || c.teams !== undefined, {
    message: 'Name at least one channel.',
  });
export type NotificationChoiceChange = z.infer<typeof NotificationChoiceChange>;

/**
 * Why a channel isn't the person's to change: always sent; required by the organization (K24);
 * the Executive group email; only in the Stock Admin daily digest; off for everyone; never used.
 */
export const ChannelLock = z.enum([
  'always',
  'required',
  'group',
  'digest',
  'organization_off',
  'not_offered',
  'with_in_app',
]);
export type ChannelLock = z.infer<typeof ChannelLock>;

export const ChannelState = z.object({ on: z.boolean(), lock: ChannelLock.nullable() });
export type ChannelState = z.infer<typeof ChannelState>;

/** One row of "Notify me about": what the notice is, and what each channel does for me now. */
export const NotificationPreferenceRow = z.object({
  event: NotificationEventKey,
  /** "Award announced". */
  name: z.string(),
  /** "On your award date, when your letter is released". */
  when: z.string(),
  /** The role it's for, when it isn't everyone's ("sector_lead"). */
  role: RoleKey.nullable(),
  email: ChannelState,
  inApp: ChannelState,
  /**
   * Posted in Teams with the in-app notice, for someone who has the MyRewards app there (AI3);
   * null when this notice isn't, or Teams isn't set up here.
   */
  teams: ChannelState.nullable(),
});
export type NotificationPreferenceRow = z.infer<typeof NotificationPreferenceRow>;

/** `GET /api/v1/me/notification-preferences` (or a person's, for a System Admin's assistance). */
export const NotificationPreferences = z.object({
  person: z.object({ id: z.uuid(), name: z.string() }),
  /** Where emails go: the work email from the directory, else the sign-in name; null with neither. */
  address: z.string().nullable(),
  rows: z.array(NotificationPreferenceRow),
  /** The Teams app is set up here, so the Teams note applies. */
  teamsAvailable: z.boolean(),
  /** When the person's choices last changed, and by whom; null when never. */
  lastChanged: z.object({ at: z.string(), by: z.string() }).nullable(),
});
export type NotificationPreferences = z.infer<typeof NotificationPreferences>;

/** One row of System administration › Notifications: the organization's switches. */
export const NotificationEmailRow = z.object({
  event: NotificationEventKey,
  name: z.string(),
  when: z.string(),
  /** Who gets it, in words ("The employee", "The Stock Admin team"). */
  who: z.string(),
  /** The audience group it's listed under. */
  group: z.enum(['employees', 'leaders', 'executives', 'stock_admins', 'payroll', 'everyone']),
  email: z.object({
    on: z.boolean(),
    /** `always`, `group` (the one address), `digest`, `not_offered`; null: a switch. */
    lock: ChannelLock.nullable(),
    /** When on, people can't turn it off (K24, Emails people can't turn off). */
    required: z.boolean(),
    /** On before anyone changed it. */
    byDefault: z.boolean(),
    /**
     * Each person starts with it off and turns it on for themselves in My preferences (APP-138
     * §D3), so on here doesn't mean everyone gets it. Optional, so older readers still fit.
     */
    personStartsOff: z.boolean().optional(),
  }),
  inApp: z.object({ on: z.boolean(), lock: ChannelLock.nullable() }),
  /** Where this notice's other switch lives, when it isn't on this table. */
  managedElsewhere: z.enum(['go_live', 'manager_nudge', 'digest']).nullable(),
  /**
   * The message in Settings › Messages that holds this notice's wording (APP-137); null for one
   * worked out on the page rather than sent (Getting started).
   */
  messageKey: z.string().nullable(),
});
export type NotificationEmailRow = z.infer<typeof NotificationEmailRow>;

/** `GET /api/v1/settings/notification-emails` (System Admins). */
export const NotificationEmails = z.object({
  rows: z.array(NotificationEmailRow),
  /** The Executive group address is set. */
  groupAddressSet: z.boolean(),
  /** The organization's time zone, which the digest's hour is in. */
  timeZone: z.string(),
});
export type NotificationEmails = z.infer<typeof NotificationEmails>;
